import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useI18n } from "@/i18n";
import api, { mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import PhoneInput from "@/components/PhoneInput";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import { Check, Car, Van, CarProfile, ArrowRight, ArrowLeft, CircleNotch, MapPin, CheckCircle, XCircle, UsersThree, Suitcase, Baby, Airplane, ShieldCheck, Drop, WifiHigh, CalendarX } from "@phosphor-icons/react";

const VEH_IMG = {
  economy: "https://customer-assets.emergentagent.com/job_zurich-airport-cab/artifacts/x5ooji4t_IMG-20260704-WA0000.jpg",
  van: "https://static.prod-images.emergentagent.com/jobs/d11c1f14-a29e-48e3-b4e1-8d772573ce5f/images/0bf1e9dc45a1894080e939c366608a58879df43c993c7fc5440be65fc5e24a63.png",
  first: "https://static.prod-images.emergentagent.com/jobs/d11c1f14-a29e-48e3-b4e1-8d772573ce5f/images/4a6f1bb5b20a7333c87fce7bbb7941d16e11e75aab59f90160f036b9295b132f.png",
  business: "https://static.prod-images.emergentagent.com/jobs/d11c1f14-a29e-48e3-b4e1-8d772573ce5f/images/70c63dbf3a7a830cd4e30f3f5263d0e0192940397ace6a8a655c7bd970e8eedb.png",
  sprinter: "https://static.prod-images.emergentagent.com/jobs/d11c1f14-a29e-48e3-b4e1-8d772573ce5f/images/105ea2e1c70d04052a481ccadc7a6b88c282dc11a2160bb6240fd615917c0ae0.png",
};
const VEH_ICON = { economy: CarProfile, van: Van, first: Car, business: Car, sprinter: Van };
const GRAD = { economy: "from-[#DCEFE7] to-white", van: "from-[#FCE8D8] to-white", first: "from-[#DEE9FB] to-white", business: "from-[#E7E2F6] to-white", sprinter: "from-[#F1EAD9] to-white" };
const AMENITIES = [
  { icon: Drop, key: "amen_water" },
  { icon: WifiHigh, key: "amen_wifi" },
  { icon: CalendarX, key: "amen_cancel" },
  { icon: Baby, key: "amen_childseat" },
];

export default function Booking() {
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const loc = useLocation();
  const trip = loc.state;

  const [step, setStep] = useState(0);
  const [vehicles, setVehicles] = useState({});
  const [vehicleClass, setVehicleClass] = useState(trip?.vehicle_class || "economy");
  const [fare, setFare] = useState(null);
  const [contact, setContact] = useState({ name: "", email: "", phone: "", flight_number: "", notes: "" });
  const [phoneCode, setPhoneCode] = useState("+41");
  const [phoneNum, setPhoneNum] = useState("");
  const setPhone = (code, num) => { setPhoneCode(code); setPhoneNum(num); setContact((c) => ({ ...c, phone: `${code} ${num}`.trim() })); };
  const flightOk = /^[A-Za-z]{2,3}\s?\d{1,4}[A-Za-z]?$/.test((contact.flight_number || "").trim());
  const [flightInfo, setFlightInfo] = useState(null);
  const [pax, setPax] = useState(trip?.passengers || 1);
  const [lug, setLug] = useState(trip?.luggage ?? 1);
  const [kids, setKids] = useState(trip?.children || 0);
  const [payment, setPayment] = useState("pay_on_arrival");
  const [cardEnabled, setCardEnabled] = useState(false);
  const [card, setCard] = useState({ number: "", expiry: "", cvc: "" });
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState(null);
  const [allPrices, setAllPrices] = useState({});
  const autoplay = useRef(Autoplay({ delay: 3000, stopOnInteraction: true, stopOnMouseEnter: true }));

  useEffect(() => {
    if (!trip) { nav("/"); return; }
    api.get("/pricing").then((r) => {
      setVehicles(r.data.vehicles);
      const keys = Object.keys(r.data.vehicles || {});
      if (keys.length && !keys.includes(vehicleClass)) setVehicleClass(keys[0]);
    });
    api.get("/payment-config").then((r) => {
      const enabled = !!r.data?.card_enabled;
      setCardEnabled(enabled);
      setPayment(enabled ? "card" : "pay_on_arrival");
    }).catch(() => setCardEnabled(false));
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!trip) return;
    api.post("/fare", { pickup_id: trip.pickup_id, dropoff_id: trip.dropoff_id, pickup_name: trip.pickup_name, dropoff_name: trip.dropoff_name, pickup_lat: trip.pickup_lat, pickup_lon: trip.pickup_lon, dropoff_lat: trip.dropoff_lat, dropoff_lon: trip.dropoff_lon, vehicle_class: vehicleClass, time: trip.time, service_type: trip.service_type || "transfer", hours: trip.hours || 0 })
      .then((r) => setFare(r.data))
      .catch((err) => { setFare(null); toast.error(err?.response?.data?.detail || t("err_both_foreign")); });
  }, [vehicleClass]); // eslint-disable-line

  // Fetch the price of EVERY vehicle class for this route, so all prices show together.
  useEffect(() => {
    if (!trip) return;
    const keys = Object.keys(vehicles);
    if (!keys.length) return;
    if ((trip.service_type || "transfer") === "hourly") {
      Promise.all(keys.map((k) =>
        api.post("/fare", { pickup_id: trip.pickup_id, pickup_name: trip.pickup_name, pickup_lat: trip.pickup_lat, pickup_lon: trip.pickup_lon, vehicle_class: k, time: trip.time, service_type: "hourly", hours: trip.hours || 0 })
          .then((r) => [k, r.data.total]).catch(() => [k, null])
      )).then((entries) => setAllPrices(Object.fromEntries(entries)));
    } else {
      api.post("/fare/vehicles", { pickup_id: trip.pickup_id, dropoff_id: trip.dropoff_id, pickup_name: trip.pickup_name, dropoff_name: trip.dropoff_name, pickup_lat: trip.pickup_lat, pickup_lon: trip.pickup_lon, dropoff_lat: trip.dropoff_lat, dropoff_lon: trip.dropoff_lon, time: trip.time, service_type: "transfer" })
        .then((r) => setAllPrices(Object.fromEntries((r.data.vehicles || []).map((v) => [v.vehicle_class, v.total]))))
        .catch(() => {});
    }
  }, [vehicles]); // eslint-disable-line

  useEffect(() => {
    const fn = (contact.flight_number || "").trim();
    if (!flightOk) { setFlightInfo(null); return; }
    const id = setTimeout(() => {
      api.get(`/flight/${encodeURIComponent(fn)}`).then((r) => setFlightInfo(r.data)).catch(() => setFlightInfo(null));
    }, 400);
    return () => clearTimeout(id);
  }, [contact.flight_number, flightOk]);

  if (!trip) return null;

  const rt = trip.round_trip ? 2 : 1;
  const steps = [t("step_vehicle"), t("step_contact"), t("step_payment"), t("step_done")];

  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/bookings", { ...trip, vehicle_class: vehicleClass, ...contact, passengers: pax, luggage: lug, children: kids, payment_method: payment, lang });
      if (payment === "card") {
        const res = await api.post("/payments/checkout", { reference: data.reference, origin_url: window.location.origin });
        window.location.href = res.data.url;
        return;
      }
      setBooking(data);
      setStep(3);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error("Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  const primaryBtn = "flex items-center gap-2 bg-[#EF5A1E] text-white font-body font-medium text-sm uppercase tracking-[0.14em] px-8 py-4 hover:bg-[#00227A] transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-40 disabled:hover:translate-y-0";
  const outlineBtn = "flex items-center gap-2 border border-[#0B1533] font-body font-medium text-sm uppercase tracking-[0.14em] px-6 py-4 hover:bg-[#0B1533] hover:text-white transition-colors duration-300";
  const inputCls = "w-full bg-white border border-zinc-200 rounded-2xl px-4 py-3 font-body text-sm focus:border-[#EF5A1E] focus:ring-1 focus:ring-[#EF5A1E] focus:outline-none transition-colors duration-200";
  const money = (v) => `CHF ${v.toFixed(2)}`;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-testid="booking-page">
      {/* Stepper */}
      <div className="flex items-center gap-2 mb-14 overflow-x-auto pb-2">
        {steps.map((s, i) => (
          <div key={i} className="flex items-center gap-2 shrink-0">
            <div className={`w-8 h-8 flex items-center justify-center font-body text-xs transition-colors duration-300 ${i < step ? "bg-[#EF5A1E] text-white" : i === step ? "bg-[#0B1533] text-white" : "bg-white text-zinc-400 border border-zinc-200"}`}>
              {i < step ? <Check size={14} weight="bold" /> : i + 1}
            </div>
            <span className={`font-body text-xs uppercase tracking-wider ${i === step ? "text-[#0B1533] font-semibold" : "text-[#5A607A]"}`}>{s}</span>
            {i < steps.length - 1 && <div className={`w-10 h-px mx-1 ${i < step ? "bg-[#EF5A1E]" : "bg-zinc-200"}`} />}
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 min-w-0">
          {step === 0 && (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} data-testid="step-vehicle">
              <Carousel opts={{ loop: true, align: "start" }} plugins={[autoplay.current]} className="w-full">
                <div className="flex items-center justify-between gap-4 mb-6 sm:mb-8">
                  <h2 className="font-heading font-light tracking-tighter text-3xl sm:text-4xl">{t("choose_vehicle")}</h2>
                  <div className="hidden sm:flex gap-2 shrink-0">
                    <CarouselPrevious className="static translate-y-0 h-10 w-10 border-zinc-200 text-[#0B1533] hover:bg-zinc-50" data-testid="veh-carousel-prev" />
                    <CarouselNext className="static translate-y-0 h-10 w-10 border-zinc-200 text-[#0B1533] hover:bg-zinc-50" data-testid="veh-carousel-next" />
                  </div>
                </div>
                <CarouselContent className="-ml-3 sm:-ml-4">
                  {Object.entries(vehicles).map(([key, v]) => {
                    const active = vehicleClass === key;
                    const price = allPrices[key] != null ? allPrices[key] * rt : allPrices[key];
                    return (
                      <CarouselItem key={key} className="pl-3 sm:pl-4 basis-[88%] sm:basis-1/2">
                        <div
                          onClick={() => setVehicleClass(key)}
                          data-testid={`vehicle-${key}`}
                          className={`h-full rounded-3xl bg-white overflow-hidden flex flex-col cursor-pointer transition-all duration-200 border-2 ${active ? "border-[#EF5A1E] elev" : "border-zinc-200 hover:border-[#C9D2FF]"}`}
                        >
                          <div className={`h-36 bg-gradient-to-b ${GRAD[key] || "from-[#EEF1FF] to-white"} overflow-hidden relative`}>
                            <img src={mediaUrl(v.image) || VEH_IMG[key] || VEH_IMG.economy} alt={v.label} className="w-full h-full object-cover" />
                            {active && <span className="absolute top-3 right-3 w-7 h-7 rounded-full bg-[#EF5A1E] flex items-center justify-center"><Check size={14} weight="bold" color="#fff" /></span>}
                          </div>
                          <div className="p-4 sm:p-5 flex flex-col flex-1">
                            <div className="flex items-start justify-between gap-2">
                              <h3 className="font-heading font-semibold text-base sm:text-lg tracking-tight text-[#0B1533] leading-tight">{v.label}</h3>
                              <span className="font-heading font-bold text-base sm:text-lg text-[#0B1533] tabular-nums shrink-0" data-testid={`veh-card-price-${key}`}>{price != null ? `CHF ${price.toFixed(0)}` : "…"}</span>
                            </div>
                            <div className="flex items-center gap-4 mt-2 font-body text-sm text-[#5A607A]">
                              <span className="flex items-center gap-1.5"><UsersThree size={17} weight="regular" /> {v.capacity}</span>
                              <span className="flex items-center gap-1.5"><Suitcase size={16} weight="regular" /> {v.luggage}</span>
                            </div>
                            <ul className="mt-4 space-y-2 flex-1">
                              {AMENITIES.map((a, i) => (
                                <li key={i} className="flex items-center gap-2 font-body text-[13px] text-[#5A607A]">
                                  <a.icon size={16} weight="regular" className="text-[#EF5A1E] shrink-0" /> {t(a.key)}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </CarouselItem>
                    );
                  })}
                </CarouselContent>
              </Carousel>
              <button data-testid="to-contact-btn" onClick={() => setStep(1)} className={primaryBtn + " mt-8"}>
                {t("continue")} <ArrowRight size={16} />
              </button>
            </motion.div>
          )}

          {step === 1 && (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} data-testid="step-contact">
              <h2 className="font-heading font-light tracking-tighter text-4xl mb-8">{t("contact_details")}</h2>
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="sm:col-span-2">
                  <label className="font-body text-[10px] uppercase tracking-[0.16em] text-[#5A607A] mb-1.5 block">{t("full_name")}</label>
                  <input data-testid="name-input" className={inputCls} value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} />
                </div>
                <div>
                  <label className="font-body text-[10px] uppercase tracking-[0.16em] text-[#5A607A] mb-1.5 block">{t("email")}</label>
                  <input type="email" data-testid="email-input" className={inputCls} value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} />
                </div>
                <div>
                  <label className="font-body text-[10px] uppercase tracking-[0.16em] text-[#5A607A] mb-1.5 block">{t("phone")}</label>
                  <PhoneInput code={phoneCode} number={phoneNum} onChange={setPhone} inputClass={inputCls} />
                </div>
                <div className="sm:col-span-2 grid grid-cols-3 gap-3" data-testid="pax-selectors">
                  {[
                    { icon: UsersThree, label: t("passengers"), val: pax, set: setPax, opts: [1, 2, 3, 4, 5, 6, 7, 8], tid: "passengers-input" },
                    { icon: Suitcase, label: t("luggage"), val: lug, set: setLug, opts: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10], tid: "luggage-input" },
                    { icon: Baby, label: t("children"), val: kids, set: setKids, opts: [0, 1, 2, 3, 4, 5], tid: "children-input" },
                  ].map((s, i) => (
                    <div key={i}>
                      <label className="font-body text-[10px] uppercase tracking-[0.16em] text-[#5A607A] mb-1.5 block">{s.label}</label>
                      <div className="relative">
                        <s.icon size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA0B0] pointer-events-none" />
                        <select data-testid={s.tid} value={s.val} onChange={(e) => s.set(Number(e.target.value))} className={inputCls + " pl-10 appearance-none cursor-pointer"}>
                          {s.opts.map((n) => <option key={n} value={n}>{n}</option>)}
                        </select>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="sm:col-span-2">
                  <label className="font-body text-[10px] uppercase tracking-[0.16em] text-[#5A607A] mb-1.5 block">{t("flight_number")}</label>
                  <div className="relative">
                    <input data-testid="flight-input" className={inputCls} value={contact.flight_number} onChange={(e) => setContact({ ...contact, flight_number: e.target.value.toUpperCase() })} placeholder="LX 318" />
                    {contact.flight_number && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2" data-testid="flight-status">
                        {flightOk ? <CheckCircle size={20} weight="fill" className="text-[#16A34A]" /> : <XCircle size={20} weight="fill" className="text-[#EF4444]" />}
                      </span>
                    )}
                  </div>
                  {contact.flight_number && (
                    <p className={`font-body text-xs mt-1.5 ${flightOk ? "text-[#16A34A]" : "text-[#EF4444]"}`}>{flightOk ? t("flight_valid") : t("flight_invalid")}</p>
                  )}
                  {flightOk && flightInfo && flightInfo.valid && (
                    <div className="mt-2 flex items-center gap-2.5 rounded-xl bg-[#EEF1FF] px-3.5 py-2.5" data-testid="flight-origin">
                      <Airplane size={16} weight="fill" className="text-[#EF5A1E] shrink-0" />
                      <p className="font-body text-xs text-[#0B1533]">
                        <span className="font-semibold">{flightInfo.airline}</span>
                        {flightInfo.origin_city ? <> · {t("flight_from")} <span className="font-semibold">{flightInfo.origin_city}{flightInfo.origin_iata ? ` (${flightInfo.origin_iata})` : ""}</span></> : null}
                        {!flightInfo.exact_route && flightInfo.origin_city ? <span className="text-[#5A607A]"> — {t("flight_est")}</span> : null}
                      </p>
                    </div>
                  )}
                </div>
                <div className="sm:col-span-2">
                  <label className="font-body text-[10px] uppercase tracking-[0.16em] text-[#5A607A] mb-1.5 block">{t("notes")}</label>
                  <textarea data-testid="notes-input" rows="3" className={inputCls} value={contact.notes} onChange={(e) => setContact({ ...contact, notes: e.target.value })} />
                </div>
              </div>
              <div className="flex gap-3 mt-8">
                <button data-testid="back-btn" onClick={() => setStep(0)} className={outlineBtn}><ArrowLeft size={16} /> {t("back")}</button>
                <button data-testid="to-payment-btn" disabled={!contact.name || !contact.email || !phoneNum || (contact.flight_number && !flightOk)} onClick={() => setStep(2)} className={primaryBtn}>
                  {t("continue")} <ArrowRight size={16} />
                </button>
              </div>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} data-testid="step-payment">
              <h2 className="font-heading font-light tracking-tighter text-4xl mb-8">{t("payment_title")}</h2>
              <div className="space-y-4">
                {[
                  ...(cardEnabled ? [{ key: "card", title: t("pay_card"), desc: t("pay_card_desc") }] : []),
                  { key: "pay_on_arrival", title: t("pay_arrival"), desc: t("pay_arrival_desc") },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    data-testid={`pay-${opt.key}`}
                    onClick={() => setPayment(opt.key)}
                    className={`w-full flex items-center gap-4 border p-5 text-left transition-all duration-300 ${payment === opt.key ? "border-[#EF5A1E] elev-subtle" : "border-zinc-200 hover:border-[#0B1533]"}`}
                  >
                    <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${payment === opt.key ? "border-[#EF5A1E]" : "border-[#E9E5DE]"}`}>
                      {payment === opt.key && <div className="w-2.5 h-2.5 rounded-full bg-[#EF5A1E]" />}
                    </div>
                    <div>
                      <p className="font-body font-semibold text-sm">{opt.title}</p>
                      <p className="font-body text-sm text-[#5A607A]">{opt.desc}</p>
                    </div>
                  </button>
                ))}

                {payment === "card" && (
                  <div className="border border-[#EF5A1E]/30 bg-[#EEF1FF] p-5 flex items-start gap-3" data-testid="card-form">
                    <ShieldCheck size={22} weight="fill" className="text-[#EF5A1E] shrink-0 mt-0.5" />
                    <p className="font-body text-sm text-[#0B1533]">{t("stripe_redirect_note")}</p>
                  </div>
                )}
              </div>
              <div className="flex gap-3 mt-8">
                <button data-testid="back-btn-2" onClick={() => setStep(1)} className={outlineBtn}><ArrowLeft size={16} /> {t("back")}</button>
                <button data-testid="confirm-btn" onClick={submit} disabled={loading} className={primaryBtn}>
                  {loading ? <CircleNotch size={16} className="animate-spin" /> : null}
                  {loading ? t("processing") : payment === "card" ? t("confirm_pay") : t("confirm_booking")}
                </button>
              </div>
            </motion.div>
          )}

          {step === 3 && booking && (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} data-testid="step-confirmed">
              <div className="w-16 h-16 bg-[#EF5A1E] flex items-center justify-center mb-6">
                <Check size={32} weight="bold" color="#fff" />
              </div>
              <h2 className="font-heading font-light tracking-tighter text-4xl">{t("booking_confirmed")}</h2>
              <p className="font-body text-[#5A607A] mt-3 text-lg">{t("confirmation_sent")}</p>
              <div className="border border-[#EF5A1E] mt-8 p-6 flex items-center justify-between elev-subtle" data-testid="confirmation-ref">
                <span className="font-body text-xs uppercase tracking-[0.16em] text-[#5A607A]">{t("ref")}</span>
                <span className="font-body font-medium text-2xl tracking-tight text-[#EF5A1E]">{booking.reference}</span>
              </div>
              <div className="flex flex-wrap gap-3 mt-8">
                <Link to={`/track?ref=${booking.reference}`} data-testid="track-link" className={primaryBtn}>{t("track_your_ride")} <ArrowRight size={16} /></Link>
                <Link to={`/manage?ref=${booking.reference}`} className={outlineBtn}>{t("view_details")}</Link>
              </div>
            </motion.div>
          )}
        </div>

        {/* Summary */}
        <div className="lg:col-span-1 min-w-0">
          <div className="border border-zinc-200 bg-[#FAFAFA] p-6 lg:sticky lg:top-28 elev-subtle" data-testid="fare-summary">
            <h3 className="font-body text-xs uppercase tracking-[0.18em] text-[#5A607A] mb-5">{t("fare_breakdown")}</h3>
            <div className="flex items-start gap-2.5 pb-5 border-b border-zinc-200 mb-5">
              <MapPin size={17} className="mt-0.5 shrink-0 text-[#EF5A1E]" />
              <div className="font-body text-sm">
                <p className="font-semibold">{trip.pickup_name}</p>
                <p className="text-[#5A607A]">→ {trip.dropoff_name}</p>
                <p className="text-[#5A607A] mt-2 font-body text-xs">{trip.date} · {trip.time} · {pax}p / {lug} {t("bags")}{kids ? ` / ${kids} ${t("children")}` : ""}</p>
                {trip.round_trip && <p className="text-[#EF5A1E] mt-1 font-body text-xs font-semibold" data-testid="roundtrip-note">↩ {t("return_trip")}: {trip.return_date} · {trip.return_time}</p>}
              </div>
            </div>
            {fare ? (
              <div className="space-y-2.5 font-body text-sm">
                {trip.service_type === "hourly" ? (
                  <>
                    <Row label={t("duration_label")} value={`${fare.hours} ${t("hours_word")}`} />
                    <Row label={t("hourly_rate_label")} value={`${money(fare.hourly_rate)} / ${t("hours_word")}`} />
                  </>
                ) : (
                  <>
                    <Row label={`${t("base_fare")} (${fare.distance_km} km)`} value={money(fare.base_fare)} />
                    <Row label={t("distance_fare")} value={money(fare.distance_fare)} />
                  </>
                )}
                {fare.night && <Row label={t("night_surcharge")} value={money(fare.night_surcharge)} />}
                {trip.round_trip && <Row label={t("return_trip")} value={`× 2`} />}
                <div className="flex justify-between items-end pt-5 border-t border-zinc-200 mt-3">
                  <span className="font-body text-xs uppercase tracking-[0.16em] text-[#5A607A]">{t("total")}</span>
                  <span className="font-heading font-medium text-3xl tracking-tight" data-testid="fare-total">{money(fare.total * rt)}</span>
                </div>
                <div className="mt-2 pt-2 space-y-1 border-t border-dashed border-zinc-200" data-testid="vat-breakdown">
                  <div className="flex justify-between font-body text-xs text-[#5A607A]"><span>{t("vat_net")}</span><span data-testid="fare-net">{money(fare.total * rt / 1.081)}</span></div>
                  <div className="flex justify-between font-body text-xs text-[#5A607A]"><span>{t("vat_label")}</span><span data-testid="fare-vat">{money(fare.total * rt - fare.total * rt / 1.081)}</span></div>
                </div>
                {trip.service_type !== "hourly" && (
                  <p className="font-body text-xs text-[#5A607A] pt-2">≈ {fare.eta_minutes} {t("minutes")}</p>
                )}
              </div>
            ) : (
              <p className="font-body text-sm text-[#5A607A]">…</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between">
    <span className="text-[#5A607A]">{label}</span>
    <span className="font-medium">{value}</span>
  </div>
);
